import { useState, useEffect } from "react";
import type { PosTempleSummary } from "../types/gis";
import { ENABLE_POS_DASHBOARD } from "../config/features";
import { useMapStore } from "../store/useMapStore";

let globalPosData: Record<string, PosTempleSummary> | null = null;
let globalFetchPromise: Promise<Record<string, PosTempleSummary> | null> | null = null;

const GCS_URL = "https://storage.googleapis.com/namma-map-407ca.firebasestorage.app/pos_latest.json";
const LOCAL_FALLBACK_URL = "/data/temples/pos_latest.json";

async function fetchPosData(): Promise<Record<string, PosTempleSummary> | null> {
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const primaryUrl = isLocal ? LOCAL_FALLBACK_URL : GCS_URL;
  const fallbackUrl = isLocal ? GCS_URL : LOCAL_FALLBACK_URL;

  try {
    const response = await fetch(primaryUrl);
    if (!response.ok) {
      throw new Error(`Primary POS fetch failed with status: ${response.status}`);
    }
    const data = await response.json();
    globalPosData = data;
    return data;
  } catch {
    try {
      const response = await fetch(fallbackUrl);
      if (!response.ok) {
        throw new Error(`Fallback POS fetch failed with status: ${response.status}`);
      }
      const data = await response.json();
      globalPosData = data;
      return data;
    } catch (fallbackError) {
      console.error("Failed to fetch POS dashboard data from all sources:", fallbackError);
      return null;
    }
  }
}

export function usePosData() {
  const activeLayer = useMapStore((state) => state.activeLayer);
  const [posData, setPosData] = useState<Record<string, PosTempleSummary> | null>(globalPosData);

  useEffect(() => {
    if (!ENABLE_POS_DASHBOARD || activeLayer !== "TEMPLES") {
      return;
    }

    if (posData) {
      return;
    }

    if (!globalFetchPromise) {
      globalFetchPromise = fetchPosData();
    }

    globalFetchPromise.then((data) => {
      setPosData(data);
    });
  }, [activeLayer, posData]);

  const getPosData = (templeId: string): PosTempleSummary | null => {
    if (!ENABLE_POS_DASHBOARD || !posData) return null;
    // Handle both padded IDs (like TM000006) and non-padded IDs if they exist
    return posData[templeId] || posData[templeId.trim()] || null;
  };

  return { getPosData };
}
